import type { AxiosInstance } from 'axios';
import type MDBDegréJour from '@/types/degréjour/degrejour';
import type MDBSemi from '@/types/ferme/semi';
import type MDBUtilisateur from '@/types/utilisateur';
import type MDBSite from '@/types/site';
import type OmniUser from '@/types/omniUser';
import type { OmniTBCustomer, OmniTBObjectTree } from '@/types/omniTBCustomer';

export function getAppVersion(): string {
  return '26.9.1'
}

export function isDevEnv(): boolean {
  if (!import.meta.env.VITE_KEYCLOAK_URL)
    return true

  return import.meta.env.VITE_KEYCLOAK_URL.includes('localhost') ||
         import.meta.env.VITE_KEYCLOAK_URL.includes('.dev.')
}

export function emptyMongoId(): string {
  return '000000000000000000000000'
}

export function notifyError(e: any) {
  console.log(e)
  if (e.message && e.message == 'Request failed with status code 403') {
    Notify.error('Vous n\'avez pas les permissions pour effectuer cette action.')
  }
  else {
    Notify.error(e)
  }

  if (e instanceof Error && 'response' in e && typeof (e as any).response === 'object' &&
     (e as any).response && 'data' in (e as any).response && (e as any).response.data) {
    Notify.error((e as any).response.data)
    console.log((e as any).response.data)
  }
}

export function msToTime(ms: number): string {
  const seconds = ms / 1000;
  const minutes = ms / (1000 * 60);
  const hours = ms / (1000 * 60 * 60);
  const days = ms / (1000 * 60 * 60 * 24);
  if (seconds < 60) return seconds.toFixed(1) + " Sec";
  else if (minutes < 60) return minutes.toFixed(1) + " Min";
  else if (hours < 24) return hours.toFixed(1) + " Hrs";
  else return days.toFixed(1) + " Jrs"
}

export function toDate_wd(date: string): string { return to_wd(new Date(date)) }
export function to_wd(date: Date): string {
  return date.toLocaleDateString('fr-CA', {
    weekday: 'long'
  })
}

export function toDate_dd_MM(date: string): string { return to_dd_MM(new Date(date)) }
export function to_dd_MM(date: Date): string {
  return date.toLocaleDateString('fr-CA', {
    day: '2-digit',
    month: 'short'
  })
}

export function toDate_dd_MMMM(date: string): string { return to_wd_dd_MMMM(new Date(date)) }
export function to_dd_MMMM(date: Date): string {
  return date.toLocaleDateString('fr-CA', {
    day: '2-digit',
    month: 'long'
  })
}

export function toDate_wd_dd_MMMM(date: string): string { return to_wd_dd_MMMM(new Date(date)) }
export function to_wd_dd_MMMM(date: Date): string {
  return date.toLocaleDateString('fr-CA', {
    weekday: 'short',
    day: '2-digit',
    month: 'long'
  })
}

export function toDate_wd_dd_MMMM_yyyy(date: string): string { return to_wd_dd_MMMM_yyyy(new Date(date)) }
export function to_wd_dd_MMMM_yyyy(date: Date): string {
  return date.toLocaleDateString('fr-CA', {
    weekday: 'short',
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  })
}

export function toDate_wd_dd_MMMM_yyyy_hh_mm_ss(date: string): string { return to_wd_dd_MMMM_yyyy_hh_mm_ss(new Date(date)) }
export function to_wd_dd_MMMM_yyyy_hh_mm_ss(date: Date): string {
  return date.toLocaleString('fr-CA', {
    weekday: 'short',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

export function toDate_wd_dd_MMMM_hh_mm(date: string): string { return to_wd_dd_MMMM_hh_mm(new Date(date)) }
export function to_wd_dd_MMMM_hh_mm(date: Date): string {
  const s = date.toLocaleString('fr-CA', {
    weekday: 'short',
    day: '2-digit',
    month: 'long'
  })

  return s + ' à ' + date.getHours() + ':' + date.getMinutes().toString().padStart(2, '0')
}

export function toLocaleDateString(date: string): string {
  return new Date(date).toLocaleDateString()
}

export function getSemiNbPlanches(semi: MDBSemi): number {
  return (semi.plancheÀ - semi.plancheDe) + 1
}

export function getSemiSuperficie(semi: MDBSemi): number {
  if (semi.superficieManuelle > 0) {
    return semi.superficieManuelle
  }
  return semi.superficie
}

export function getSemiDateRécoltePrévue(semi: MDBSemi) {
  const date = new Date(semi.dateDu)

  const dateManuelle = new Date(semi.dateRécoltePrévueManuelle)
  if (dateManuelle > date) {
    return semi.dateRécoltePrévueManuelle
  }

  if (semi.degréJour != null && semi.degréJour != undefined) {
    const dateAJ = new Date(semi.degréJour.dateRécoltePrévue)
    if (dateAJ.getFullYear() == date.getFullYear()) {
      return semi.degréJour.dateRécoltePrévue
    }

    const dateRP = new Date(semi.degréJour.dateRécoltePrévueInitiale)
    if (dateRP.getFullYear() == date.getFullYear()) {
      return semi.degréJour.dateRécoltePrévueInitiale
    }
  }

  const storeU = useUtilisateurStore()
  const culture = storeU.cultureSélectionnée.nom
  if (culture == 'Iceberg / Pommée' || culture == 'Laitue Romaine' || culture == 'Radis Chinois') {
    if (storeU.siteSélectionné.accès.includes('degréjours')) {
      return '';
    }
  }

  const dateR = new Date(semi.dateRécoltePrévue)
  if (dateR.getFullYear() == date.getFullYear()) {
    return semi.dateRécoltePrévue
  }

  return ''
}

export function getSemiVérifiéTextColor(semi: MDBSemi) {
  const level = getSemiVérifiéLevel(semi)
  if (level == 2) {
    return 'text-orange-lighten-1'
  } else if (level == 3) {
    return 'text-red-lighten-1'
  }
  return ''
}

export function getSemiVérifiéColor(semi: MDBSemi | undefined) {
  const level = getSemiVérifiéLevel(semi)
  if (level == 2) {
    return '#ff9800' // Orange
  } else if (level == 3) {
    return '#f44336' // Red
  }
  return '#4caf50' // Green
}

export function getSemiVérifiéLevel(semi: MDBSemi | undefined) {
  if (semi == undefined) {
    return 3
  }
  const dateVérification = semi.dateVérification
  if (dateVérification == undefined || dateVérification == null || dateVérification == '')
    return 3

  const date = new Date(dateVérification)
  if (date.getFullYear() <= 2000) {
    return 3
  }

  if (!semi.fermé) {
    const now = new Date()
    const diff = Math.abs(now.getTime() - date.getTime())
    const diffDays = Math.ceil(diff / (1000 * 3600 * 24))
    if (diffDays >= 7) {
      return 3
    } else if (diffDays >= 5) {
      return 2
    }
  }

  return 1
}

export function getSemiÉchantillonColor(semi: MDBSemi | undefined) {
  if (semi == undefined) {
    return 'gray'
  }

  const date = new Date(semi.échantillonDate)
  if (date.getFullYear() <= 2000) {
    return 'gray'
  }

  switch (semi.échantillonStatut) {
    case 'Date estimée':
      return 'gray'
    case 'En analyse':
      return 'yellow'
    case 'Présomptif':
      return 'orange'
    case 'Contaminé':
      return 'red'
    case 'OK': {
        // If more than 7 days, purple
        if (date) {
          const now = new Date()
          now.setHours(12, 0, 0, 0)
          const diff = Math.abs(now.getTime() - date.getTime())
          const diffDays = diff / (1000 * 3600 * 24)
          if (diffDays > 7.1) {
            return 'purple'
          }
        }
        return 'blue'
      }
    default:
      return 'gray'
  }
}

export function stringArrayEqual(array1: string[] | undefined, array2: string[] | undefined): boolean {
  if (array1 == undefined || array2 == undefined) {
    return false
  }
  if (array1.length !== array2.length) {
    return false
  }
  for (let i = 0; i < array1.length; i++) {
    if (array1[i] !== array2[i]) {
      return false
    }
  }
  return true
}

export function getDJMoyenne(dj: MDBDegréJour): number {
  return (dj.températureMax + dj.températureMin) / 2
}

export function calculDegréJour(dj: MDBDegréJour, base: number): number {
  return Math.max(0, getDJMoyenne(dj) - base)
}

export function getNomRéduit(utilisateur: MDBUtilisateur): string {
  return utilisateur.prénom + ' ' + utilisateur.nom[0]
}

export function roundTo(n : number, digits : number) : number {
  if (digits === undefined) {
    digits = 0;
  }

  const negative = n < 0;
  if (negative) {
    n = -n
  }

  const multiplicator = Math.pow(10, digits)
  n = +(parseFloat((n * multiplicator).toFixed(11)))
  n = +((Math.round(n) / multiplicator).toFixed(digits))
  if (negative) {
      n = +((-n).toFixed(digits))
  }

  return n;
}

export function computeSelectionSum() {
  const selection = window.getSelection()?.toString()
  if (selection) {
    // Find all numbers (integers or decimals, positive or negative)
    const numbers = selection.match(/-?\d+(\.\d+)?/g)?.map(Number) || []
    if (numbers.length > 0) {
      return roundTo(numbers.reduce((a, b) => a + b, 0), 2)
    }
  }

  return null
}

export function isInAccessList(list: string[] | undefined, accessTo: string): boolean {
  if (!list || list.length === 0) {
    return false
  }

  if (list.includes(accessTo)) {
    return true
  }

  const tokens = accessTo.split('.')
  if (tokens.length > 1) {
    // Check everything but last item, it's already checked with contains above
    let token = tokens[0]
    for (let i = 0; i < tokens.length - 1; ++i) {
      if (list.includes(token)) {
        return true
      }
      token += `.${tokens[i + 1]}`
    }
  }

  return false
}

export function siteHasAccess(site: MDBSite, access: string) {
  if (!site || !site.accès || !site.accèsNon) {
    return false
  }

  if (!access || access.length === 0) {
    return true
  }

  return !isInAccessList(site.accèsNon, access) &&
         isInAccessList(site.accès, access)
}

declare interface LogOTTResult {
  apiKey: string
  tbToken: string
}

export async function logOTT(apiAxios: AxiosInstance, ottToken: string | null): Promise<LogOTTResult | null> {
  try {
    let apiKey = ''
    let tbToken = ''

    if (ottToken) {
      const config = { params: { ott: ottToken } }
      const ott = await apiAxios.get('api/api/ott', config)
      //console.log('OTT Response:', ott)
      apiKey = ott.data.aPIKey
      tbToken = ott.data.thingsBoardBearerToken
    } else if (import.meta.env.VITE_OMNI_TEST_APIKEY) {
      //console.log('Using test API key:', import.meta.env.VITE_OMNI_TEST_APIKEY)
      apiKey = import.meta.env.VITE_OMNI_TEST_APIKEY
      const config = { headers: { 'X-APIKey': apiKey } }
      const tbResponse = await apiAxios.post('api/tb/login', null, config)
      //console.log('ThingsBoard Login Response:', tbResponse)
      tbToken = 'Bearer ' + tbResponse.data.token
    }

    if (!apiKey || !tbToken) {
      Notify.error("Erreur d'authentification.")
      return null
    }

    Notify.success("Authentification réussie.")
    return { apiKey, tbToken }

  } catch (e) {
    notifyError(e)
    return null
  }
}

declare interface FullLogOTTResult {
  error: string | null
  apiKey: string
  tbToken: string
  user: OmniUser | null
  customer: OmniTBCustomer | null
}

export async function fullLogOTT(apiAxios: AxiosInstance, window: Window, customerId: string | null): Promise<FullLogOTTResult> {

  const r: FullLogOTTResult = { error: null, apiKey: '', tbToken: '', user: null, customer: null }

  // Get ott token from query params
  const urlParams = new URLSearchParams(window.location.search)
  const ottToken = urlParams.get('ott')
  //console.log('OTT Token:', ottToken)

  // Log with ott token
  const results = await logOTT(apiAxios, ottToken)
  if (results == null || !results.apiKey || !results.tbToken) {
    r.error = "Erreur d'authentification."
    return r
  }

  r.apiKey = results.apiKey
  r.tbToken = results.tbToken

  // Get user info
  try {
    const config = { headers: { 'X-APIKey': r.apiKey } }
    const userResponse = await apiAxios.get('api/api/user', config)
    //console.log('User Response:', userResponse.data)
    r.user = userResponse.data as OmniUser

    const custId = customerId || r.user.defaultCustomerId

    if (!r.user || !custId) {
      r.error = "Utilisateur ou client non trouvé."
      return r
    }

    const config2 = { headers: { 'X-APIKey': r.apiKey, 'X-ThingsBoard': r.tbToken } }
    const customerResponse = await apiAxios.get(`api/tb/customer/${custId}/attributes`, config2)
    //console.log('Customer Attributes Response:', customerResponse.data)
    r.customer = customerResponse.data as OmniTBCustomer

    if (!r.customer) {
      r.error = "Client non trouvé."
      return r
    }
  }
  catch (e) {
    notifyError(e)
    r.error = "Erreur lors de la récupération des informations utilisateur ou client."
    return r
  }

  return r
}

export function getDevicesFromTree(tree: OmniTBObjectTree, type: string | null): OmniTBObjectTree[] {
  const devices: OmniTBObjectTree[] = []
  const collect = (node: OmniTBObjectTree) => {
    if (node.id != null && node.id.entityType === 'DEVICE' && (type === null || node.type === type)) {
      devices.push(node)
    }
    node.children?.forEach(collect)
  }
  collect(tree)
  return devices
}

function normalizeType(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

export function getAssetsFromTree(tree: OmniTBObjectTree, type: string | null): OmniTBObjectTree[] {
  const assets: OmniTBObjectTree[] = []
  const normalizedType = type != null ? normalizeType(type) : null
  const collect = (node: OmniTBObjectTree) => {
    if (node.id != null && node.id.entityType === 'ASSET' && (normalizedType === null || normalizeType(node.type) === normalizedType)) {
      assets.push(node)
    }
    node.children?.forEach(collect)
  }
  collect(tree)
  return assets
}
